import { defineQuery } from "@rocicorp/zero";
import { z } from "zod";
import type {} from "../zero-context";
import { zql } from "../schema";
import { channelMemberGuard } from "./access-filters";

export const messagesQueries = {
  // Paginated channel messages with related data — iOS-only after the desktop
  // composite cutover (Phase 2). Desktop uses `byChannelComposite`; iOS still
  // subscribes via `ZeroDataClient.swift` until its kind-dispatch renderer
  // and composite-shape adoption land.
  byChannelPaginatedWithRelated: defineQuery(
    z.object({
      channelId: z.string(),
      limit: z.number().default(50),
    }),
    ({ args: { channelId, limit }, ctx: { sub } }) =>
      zql.messages
        .where("channel_id", channelId)
        .where("thread_id", "IS", null)
        .whereExists("channel", channelMemberGuard(sub))
        .orderBy("created_at", "desc")
        .orderBy("id", "desc")
        .limit(limit)
        .related("sender")
        .related("reactions", (q) => q.related("user"))
        .related("attachments")
        .related("link_previews")
        .related("calendar_attendees")
        .related("email_participants")
        .related("call_participants")
        // Phase 7c-D1b: hydrate the `message_mentions` junction so iOS
        // and desktop callers can source mentions from the relational
        // table instead of the legacy `messages.mentions` JSONB column.
        .related("message_mentions"),
  ),
  // Single composite for the desktop MessageArea — parent-message rows +
  // sender + reactions(+user) + attachments + link_previews + side-table
  // junctions (calendar_attendees / email_participants / call_participants)
  // in one subscription. Powers the channel-render path; iOS still uses the
  // separate `byChannelPaginatedWithRelated` path.
  byChannelComposite: defineQuery(
    z.object({
      channelId: z.string(),
      limit: z.number().default(50),
    }),
    ({ args: { channelId, limit }, ctx: { sub } }) =>
      zql.messages
        .where("channel_id", channelId)
        .where("thread_id", "IS", null)
        .whereExists("channel", channelMemberGuard(sub))
        .orderBy("created_at", "desc")
        .orderBy("id", "desc")
        .limit(limit)
        .related("sender")
        .related("reactions", (q) => q.related("user"))
        .related("community_signals")
        .related("attachments")
        .related("link_previews")
        .related("calendar_attendees")
        .related("email_participants")
        .related("call_participants")
        // Phase 7c-D1b: hydrate the `message_mentions` junction so the
        // channel-render path can derive `MessageData.mentions` from
        // the junction instead of `messages.mentions` JSONB.
        .related("message_mentions"),
  ),
  byThread: defineQuery(z.string(), ({ args: threadId, ctx: { sub } }) =>
    zql.messages
      .where("thread_id", threadId)
      .whereExists("channel", channelMemberGuard(sub))
      .orderBy("created_at", "asc")
      .orderBy("id", "asc"),
  ),
  // Phase 3 ThreadPanel composite — single subscription returning the
  // thread parent (matched by id) + all replies (via the new
  // `thread_replies` self-relationship) + thread_followers + every
  // child-table relation per message. Replaces ThreadPanel's three
  // separate `useQuery` calls (threadParent, threadReplies,
  // thread_followers.byThread) with one materialization shared via
  // ViewCache. Same access guard as the rest of the channel-render path
  // (`channelMemberGuard`) — applied at the parent level since replies
  // inherit the parent's channel by FK.
  byThreadComposite: defineQuery(
    z.string(),
    ({ args: threadId, ctx: { sub } }) =>
      zql.messages
        .where("id", threadId)
        .whereExists("channel", channelMemberGuard(sub))
        .related("sender")
        .related("reactions", (q) => q.related("user"))
        .related("community_signals")
        .related("attachments")
        .related("link_previews")
        .related("calendar_attendees")
        .related("email_participants")
        .related("call_participants")
        .related("thread_followers")
        // Phase 7c-D1b: junction-source mentions for thread parent +
        // every reply. Matches the per-message `byIdWithRelated` shape.
        .related("message_mentions")
        // Discord-shape: when the thread parent is itself an inline
        // reply, hydrate the cited source so ThreadPanel can render
        // the citation pill above the parent. One sub stays one sub.
        .related("quoted_source", (q) => q.related("sender"))
        .related("thread_replies", (q) =>
          q
            .orderBy("created_at", "asc")
            .orderBy("id", "asc")
            .related("sender")
            .related("reactions", (rq) => rq.related("user"))
            .related("community_signals")
            .related("attachments")
            .related("link_previews")
            .related("calendar_attendees")
            .related("email_participants")
            .related("call_participants")
            .related("message_mentions"),
        )
        .one(),
  ),
  // Compound query: thread messages with sender, reactions, attachments,
  // link_previews, calendar_attendees, email_participants, call_participants.
  // Mirrors the channel-list relation set so card replies hydrate side-table
  // arrays per-message (Phase 6).
  byThreadWithRelated: defineQuery(
    z.string(),
    ({ args: threadId, ctx: { sub } }) =>
      zql.messages
        .where("thread_id", threadId)
        .whereExists("channel", channelMemberGuard(sub))
        .orderBy("created_at", "asc")
        .orderBy("id", "asc")
        .related("sender")
        .related("reactions", (q) => q.related("user"))
        .related("community_signals")
        .related("attachments")
        .related("link_previews")
        .related("calendar_attendees")
        .related("email_participants")
        .related("call_participants")
        // Phase 7c-D1b: junction-source mentions for thread replies.
        .related("message_mentions"),
  ),
  // Original allDesc - deprecated, use recentMessages instead
  allDesc: defineQuery(({ ctx: { sub } }) =>
    zql.messages
      .whereExists("channel", channelMemberGuard(sub))
      .orderBy("created_at", "desc"),
  ),
  // Limited recent messages, capped at 10000. Sole live consumer
  // after the cross-channel mention banner removal is `CommandPalette`
  // (`apps/desktop/src/components/CommandPalette.tsx`), gated on a
  // user-typed search query — not always-on.
  // Phase 7c-D1a: hydrates `message_mentions` so consumers can source
  // mentions from the junction instead of the `messages.mentions`
  // JSONB column.
  recentMessages: defineQuery(
    z.number().default(10000),
    ({ args: limit, ctx: { sub } }) =>
      zql.messages
        .whereExists("channel", channelMemberGuard(sub))
        .orderBy("created_at", "desc")
        .limit(limit)
        .related("message_mentions"),
  ),
  /** @deprecated Use recentMessages */
  recentByUserMemberships: defineQuery(
    z.number().default(10000),
    ({ args: limit, ctx: { sub } }) =>
      zql.messages
        .whereExists("channel", channelMemberGuard(sub))
        .orderBy("created_at", "desc")
        .limit(limit),
  ),
  // Recent messages within a time window (for efficient unread counting)
  recentByDays: defineQuery(
    z.number().default(30),
    ({ args: days, ctx: { sub } }) => {
      const cutoff = Date.now() - days * 24 * 60 * 60 * 1000;
      return zql.messages
        .where("created_at", ">", cutoff)
        .whereExists("channel", channelMemberGuard(sub))
        .orderBy("created_at", "desc");
    },
  ),
  byId: defineQuery(z.string(), ({ args: id, ctx: { sub } }) =>
    zql.messages
      .where("id", id)
      .whereExists("channel", channelMemberGuard(sub))
      .one(),
  ),
  // Compound query: single message with all related data (useful for thread parent).
  // Phase 7c-D1a: includes `message_mentions` so the MessageArea outbound-payload
  // builder can source mentions from the junction instead of `messages.mentions` JSONB.
  byIdWithRelated: defineQuery(z.string(), ({ args: id, ctx: { sub } }) =>
    zql.messages
      .where("id", id)
      .whereExists("channel", channelMemberGuard(sub))
      .related("sender")
      .related("reactions", (q) => q.related("user"))
      .related("community_signals")
      .related("attachments")
      .related("link_previews")
      .related("calendar_attendees")
      .related("email_participants")
      .related("call_participants")
      .related("message_mentions")
      .one(),
  ),
  withReplies: defineQuery(z.string(), ({ args: channelId, ctx: { sub } }) =>
    zql.messages
      .where("channel_id", channelId)
      .where("thread_id", "IS", null)
      .where("reply_count", ">", 0)
      .whereExists("channel", channelMemberGuard(sub))
      .orderBy("latest_reply_at", "desc"),
  ),
  broadcasts: defineQuery(z.string(), ({ args: channelId, ctx: { sub } }) =>
    zql.messages
      .where("channel_id", channelId)
      .where("is_broadcast", true)
      .whereExists("channel", channelMemberGuard(sub))
      .orderBy("created_at", "desc"),
  ),
  // Recent call-lifecycle messages in channels the user belongs to
  // (drives the incoming-call detector). Filters by typed `kind` —
  // replaces the legacy `is_system_message=true + content-string match`
  // path. The old `recentCallSystemMessages` name is retired.
  recentCallMessages: defineQuery(
    z.number().default(10),
    ({ args: limit, ctx: { sub } }) =>
      zql.messages
        .where("kind", "IN", [
          "call_started_video",
          "call_started_audio",
          "call_ended",
        ])
        .whereExists("channel", channelMemberGuard(sub))
        .orderBy("created_at", "desc")
        .limit(limit)
        .related("sender")
        .related("channel"),
  ),
  // Message-derived inbox feed. Replaces the 99.988% of notification_events
  // rows that were message-backed (reason IN channel/dm/mention) — those
  // rows no longer flow to clients after PR 2's publication row filter.
  // The three branches cover the same access paths the trigger encoded:
  //   - DMs where I'm an active member (DM / group_DM channel types).
  //   - Messages with an explicit @mention of me (message_mentions).
  //   - Messages in channels where I set notify='all' (+ active member).
  // Filters skip system / call-* kinds and messages I authored. Results
  // are sorted recent-first and capped at 200; the inbox UI further
  // filters by `created_at > user_channel_stats.last_read_at` client-side
  // (was on cm.last_read_at pre-P6-D1; Zero can't express that
  // correlated predicate cleanly).
  inboxForUser: defineQuery(({ ctx: { sub } }) =>
    zql.messages
      .where("user_id", "!=", sub)
      .where("kind", "NOT IN", [
        "system",
        "call_started_video",
        "call_started_audio",
        "call_ended",
      ])
      // Channel-membership guard applied universally so no branch
      // (including mention) can match a message from a channel I am
      // not in. Without this, `actions.sendMessage` accepting arbitrary
      // mention user IDs + the unconditional junction-population path
      // would let an attacker in a private channel leak message
      // content to any user by @-mentioning them. See the iOS SQL
      // mirror in ZeroDataClient.swift for the equivalent JOIN.
      .whereExists("channel", channelMemberGuard(sub))
      .where((eb) =>
        eb.or(
          // DMs: channel is dm/group_dm AND I'm an active member.
          eb.exists("channel", (cq) =>
            cq
              .where("type", "IN", ["dm", "group_dm"])
              .whereExists("members", (mq) =>
                mq.where("user_id", sub).where("removed_at", "IS", null),
              ),
          ),
          // Direct @mention of me (message_mentions junction). Populated
          // by builders (INSERT) and `syncMessageMentionsJunction`
          // (UPDATE) — the legacy trigger was dropped in migration 117.
          // Backfilled for historical pre-trigger-drop rows in 104.
          // Security: the outer channelMemberGuard above ensures we
          // only match messages in channels I'm a member of, so
          // attacker-controlled mentions of non-members can't leak
          // message content across channel boundaries.
          eb.exists("message_mentions", (mm) => mm.where("user_id", sub)),
          // notify='all' channel member (active).
          eb.exists("channel", (cq) =>
            cq.whereExists("members", (mq) =>
              mq
                .where("user_id", sub)
                .where("notify", "all")
                .where("removed_at", "IS", null),
            ),
          ),
        ),
      )
      .orderBy("created_at", "desc")
      .orderBy("id", "desc")
      .limit(200)
      .related("sender")
      .related("channel")
      // Phase 7c-D1a: hydrate junction rows so InboxView can classify
      // message-derived inbox items by mention without consulting the
      // legacy `messages.mentions` JSONB column.
      .related("message_mentions"),
  ),
};

export type ThreadRepliesArgs = {
  threadId: string;
};
